// Full-screen picker for selecting a rectangular excerpt from a score's PDF.
// Renders the PDF via react-pdf, lets the user drag a rectangle on a page,
// and on confirm produces a high-resolution PNG File of the selected region
// — ready for the OMR pipeline.
//
// Coordinates note:
//   The user's drag is captured in CSS pixels relative to the rendered <Page>.
//   On confirm, we re-render the page to a separate canvas at a fixed high
//   scale (DEFAULT_CROP_RENDER_SCALE) and translate the CSS-pixel rect into
//   canvas-pixel coordinates via the ratio between the canvas size and the
//   measured CSS size. This decouples display zoom from output resolution.

import React, { useEffect, useRef, useState } from "react";
import { Document, Page } from "react-pdf";
import { ChevronLeft, ChevronRight, X, Loader } from "lucide-react";
import { getScoreMetadata, loadScorePDF } from "../../utils/scoreStorage";
import { renderPdfPageToCanvas, cropCanvasToPngBlob } from "../../utils/pdfCropUtils";

const MIN_SELECTION_PX = 20;

export default function ScoreExcerptPicker({
  userId,
  score, // { id, title, ... } — must at least have id
  onExcerptSelected,
  onClose,
}) {
  const [pdfBuffer, setPdfBuffer] = useState(null);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageCssDims, setPageCssDims] = useState(null); // { width, height }
  const [selection, setSelection] = useState(null); // { x, y, w, h } in CSS px
  const [dragStart, setDragStart] = useState(null); // { x, y }
  const [submitting, setSubmitting] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const pageRef = useRef(null);

  // Load PDF buffer + create a blob URL for react-pdf rendering.
  useEffect(() => {
    if (!userId || !score?.id) return;
    let cancelled = false;
    let blobUrl = null;

    (async () => {
      try {
        const meta = await getScoreMetadata(userId, score.id);
        if (!meta || !meta.storagePath) {
          throw new Error("Score is missing its storage path. Try re-uploading it.");
        }
        const url = await loadScorePDF(userId, score.id, meta.storagePath);
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        blobUrl = url;
        setPdfBlobUrl(url);

        // Also fetch as ArrayBuffer for the high-resolution crop render.
        const resp = await fetch(url);
        const buffer = await resp.arrayBuffer();
        if (cancelled) return;
        setPdfBuffer(buffer);
      } catch (err) {
        if (cancelled) return;
        console.error("ScoreExcerptPicker: failed to load PDF", err);
        setLoadError(err.message || "Could not load score PDF.");
      }
    })();

    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [userId, score?.id]);

  // Reset selection when the user changes pages.
  useEffect(() => {
    setSelection(null);
    setDragStart(null);
  }, [pageNumber]);

  function getRelativeCoords(e) {
    const rect = pageRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    return {
      x: clamp(e.clientX - rect.left, 0, rect.width),
      y: clamp(e.clientY - rect.top, 0, rect.height),
    };
  }

  function onPointerDown(e) {
    if (submitting) return;
    e.preventDefault();
    e.target.setPointerCapture?.(e.pointerId);
    const start = getRelativeCoords(e);
    setDragStart(start);
    setSelection({ x: start.x, y: start.y, w: 0, h: 0 });
  }

  function onPointerMove(e) {
    if (!dragStart) return;
    const cur = getRelativeCoords(e);
    setSelection({
      x: Math.min(dragStart.x, cur.x),
      y: Math.min(dragStart.y, cur.y),
      w: Math.abs(cur.x - dragStart.x),
      h: Math.abs(cur.y - dragStart.y),
    });
  }

  function onPointerUp() {
    setDragStart(null);
  }

  const selectionTooSmall =
    !selection || selection.w < MIN_SELECTION_PX || selection.h < MIN_SELECTION_PX;

  async function handleConfirm() {
    if (selectionTooSmall || !pdfBuffer || !pageCssDims) return;
    setSubmitting(true);
    try {
      const canvas = await renderPdfPageToCanvas(pdfBuffer, pageNumber);
      const scaleX = canvas.width / pageCssDims.width;
      const scaleY = canvas.height / pageCssDims.height;
      const blob = await cropCanvasToPngBlob(
        canvas,
        selection.x * scaleX,
        selection.y * scaleY,
        selection.w * scaleX,
        selection.h * scaleY
      );
      const safeTitle = (score.title || "score").replace(/[^\w\-. ]+/g, "_");
      const file = new File(
        [blob],
        `${safeTitle}-excerpt-p${pageNumber}.png`,
        { type: "image/png" }
      );
      onExcerptSelected(file);
    } catch (err) {
      console.error("ScoreExcerptPicker: crop failed", err);
      alert("Could not prepare the excerpt image: " + err.message);
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[200] flex flex-col bg-gray-900/95"
      role="dialog"
      aria-modal="true"
      aria-label="Choose excerpt from score"
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-3 bg-gray-800 text-white shadow">
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="text-gray-300 hover:text-white disabled:opacity-50"
            aria-label="Close"
          >
            <X size={22} />
          </button>
          <h3 className="text-base font-semibold truncate">
            {score.title || "Score"} — choose an excerpt
          </h3>
        </div>
        <div className="flex items-center gap-2">
          {numPages > 1 && (
            <>
              <button
                type="button"
                onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
                disabled={pageNumber <= 1 || submitting}
                className="p-1 rounded hover:bg-gray-700 disabled:opacity-40"
                aria-label="Previous page"
              >
                <ChevronLeft size={20} />
              </button>
              <span className="text-sm tabular-nums">
                Page {pageNumber} / {numPages}
              </span>
              <button
                type="button"
                onClick={() => setPageNumber((p) => Math.min(numPages, p + 1))}
                disabled={pageNumber >= numPages || submitting}
                className="p-1 rounded hover:bg-gray-700 disabled:opacity-40"
                aria-label="Next page"
              >
                <ChevronRight size={20} />
              </button>
            </>
          )}
          <button
            type="button"
            onClick={handleConfirm}
            disabled={selectionTooSmall || submitting || !pdfBuffer}
            className="ml-3 px-4 py-1.5 rounded bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium disabled:bg-gray-600 disabled:cursor-not-allowed"
          >
            {submitting ? "Preparing…" : "Use this excerpt"}
          </button>
        </div>
      </div>

      {/* Page area */}
      <div className="flex-1 overflow-auto flex items-start justify-center p-6">
        {loadError && (
          <div className="text-red-300 mt-12">{loadError}</div>
        )}
        {!loadError && !pdfBlobUrl && (
          <div className="flex items-center gap-2 text-gray-300 mt-12">
            <Loader className="animate-spin" size={18} />
            Loading score…
          </div>
        )}
        {!loadError && pdfBlobUrl && (
          <Document
            file={pdfBlobUrl}
            onLoadSuccess={({ numPages: n }) => setNumPages(n)}
            onLoadError={(err) =>
              setLoadError("Could not render PDF: " + err.message)
            }
            loading={
              <div className="flex items-center gap-2 text-gray-300 mt-12">
                <Loader className="animate-spin" size={18} />
                Loading score…
              </div>
            }
          >
            <div
              ref={pageRef}
              className="relative shadow-xl select-none touch-none"
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerUp}
              style={{ cursor: submitting ? "wait" : "crosshair" }}
            >
              <Page
                pageNumber={pageNumber}
                width={Math.min(900, window.innerWidth - 64)}
                renderTextLayer={false}
                renderAnnotationLayer={false}
                onRenderSuccess={(page) => {
                  // page.width / page.height are in CSS pixels matching the
                  // displayed canvas (react-pdf already accounts for our
                  // chosen `width` prop).
                  setPageCssDims({ width: page.width, height: page.height });
                }}
              />
              {selection && (
                <div
                  className="absolute border-2 border-blue-400 bg-blue-400/20 pointer-events-none"
                  style={{
                    left: selection.x,
                    top: selection.y,
                    width: selection.w,
                    height: selection.h,
                  }}
                />
              )}
            </div>
          </Document>
        )}
      </div>

      {/* Footer hint */}
      <div className="px-4 py-2 bg-gray-800 text-gray-300 text-xs text-center">
        Drag a rectangle around the measures you want to import. Smaller
        excerpts are usually more accurate.
      </div>
    </div>
  );
}

function clamp(n, min, max) {
  return Math.min(Math.max(n, min), max);
}
